/**
 * 本文を書いて送る: 件名・本文（テキストだけ = 最小の形。HTML は後で）・宛先の絞り込みと宛先の数・フッターの見本・
 * 送り方（今すぐ / 予約 / 定期）・確認のダイアログ。
 *
 * API（Core の確定した形）: POST …/mail/broadcasts `{ subject, text, filters?, sendAt?, recurrence? }` → `{ broadcast }`
 *   sendAt なし = 下書き → 「今すぐ」は続けて POST …/broadcasts/:id/send。sendAt あり = 予約。recurrence = 定期。
 *   宛先の数は GET …/mail/audience?language=&lastPlayedWithinDays=（数だけ・アドレスは返らない）。
 *
 * 時刻: 入力はブラウザの現地時刻、送るときに UTC に直す（mailLogic.localToUtcIso）。画面にも「あなたの時刻（タイムゾーン名）」を出す。
 * 送る操作は blocks（送れない理由）が 1 つでもあれば出さない（差出人が揃うまで・停止中は、ボタンの代わりに理由を出す）。
 *
 * 送り始めの 1 日の上限（warmup・2026-09-27 creator/mail-warmup）: 「送るとき」の欄に上限の帯（WarmupBand）、確認のダイアログに
 *   「一部は翌日以降に送られます」（今日の残りを超えるとき）、送った後に「{deferred} 通は {nextDayAt} から自動で続けて送ります」。
 *   超えた分はサーバーが断らずに翌日へ回すので、どれも「止める」ではなく「知らせる」だけ（送るボタンは押せるまま）。
 */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, newIdempotencyKey } from '../../api';
import { ConfirmDialog, fullDate, num } from '../../components/ui';
import { useResource } from '../../hooks';
import { useT, type MessageKey } from '../../i18n';
import type { Broadcast, MailSender } from '../../mailApi';
import { limitKind, overToday, sendDeferred, type MailWarmup } from '../../mailWarmup';
import {
  audienceCount,
  audienceQuery,
  buildBroadcast,
  defaultLocalSlot,
  MAIL_LIMITS,
  nowBlocked,
  observesDst,
  unwrap,
  type BroadcastBody,
  type ComposeField,
  type ComposeForm,
  type ComposeProblem,
  type Every,
  type MailBlock,
  type SendMode
} from '../../mailLogic';
import { everyLabel, mailErrorText } from './mailDisplay';
import { WarmupBand } from './WarmupBand';

/**
 * 言語の絞り込みの候補（プレイヤーの言語設定の値）。'' = すべて。自由入力にしない = 打ち間違いで 0 人になる、を防ぐ。
 * ja / en だけ（2026-09-27 mail-align）: Core の filters.language は 'en' | 'ja' だけを受ける（プレイヤーの locale が en / ja で、
 *   フッターもこの 2 つ）。以前は zh / ko / es … も並べていたが、選ぶと 400 で戻される = Core が言語を増やしたらここに足す
 */
const LANGUAGES = ['', 'ja', 'en'] as const;
/** 最後に遊んだ日の絞り込み（日数）。null = 絞らない。長く遊んでいない人に送ると苦情が増えやすいので、短い期間を選べるようにする */
const LAST_PLAYED = [null, 7, 30, 90, 365] as const;

const PROBLEM_KEYS: Record<ComposeProblem, MessageKey> = {
  required: 'mail.problem.required',
  tooLong: 'mail.problem.tooLong',
  newline: 'mail.problem.newline',
  invalid: 'mail.problem.invalidTime',
  past: 'mail.problem.past',
  tooFar: 'mail.problem.tooFar',
  monthDay: 'mail.problem.monthDay'
};
const BLOCK_KEYS: Record<Exclude<MailBlock, 'kids' | 'feature_off'>, MessageKey> = {
  suspended: 'mail.compose.blocked.suspended',
  sender_incomplete: 'mail.compose.blocked.sender'
};

/** ブラウザのタイムゾーン名（Asia/Tokyo など）。取れない環境では空 = 「あなたの時刻」だけ出す */
const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
};

export function ComposeCard({
  mail, gameName, sender, sendingDomain, warmup, blocks, onSenderIncomplete, onChanged
}: {
  mail: string; gameName: string; sender: MailSender | null; sendingDomain: string | null; warmup: MailWarmup | null; blocks: MailBlock[];
  onSenderIncomplete: () => void; onChanged: () => void;
}) {
  const { t, tr } = useT();
  const [form, setForm] = useState<ComposeForm>(() => ({
    subject: '', text: '', language: '', lastPlayedWithinDays: null, mode: 'now', every: 'week', ...defaultLocalSlot(new Date())
  }));
  const [issues, setIssues] = useState<Array<{ field: ComposeField; problem: ComposeProblem }>>([]);
  const [pending, setPending] = useState<{ body: BroadcastBody; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  // deferred = 今すぐ送るの応答の warmup.deferred（送り始めの上限で翌日に回った数と、続きを送り始める時刻）。回っていなければ null
  const [done, setDone] = useState<{ mode: SendMode; at: string | null; deferred: ReturnType<typeof sendDeferred> } | null>(null);
  // 同じ入力の「作成」を二重にしない: 作成は済んで送信で失敗したとき、もう一度押すと下書きがもう 1 通できる → 作った id を覚えて送信だけやり直す
  const createdRef = useRef<{ key: string; id: string } | null>(null);

  const q = audienceQuery(form);
  const audience = useResource<unknown>(`${mail}/audience${q}`);
  const recipients = audience.data ? audienceCount(audience.data) : null;
  const tz = useMemo(browserTimeZone, []);
  const dst = useMemo(() => observesDst(new Date().getFullYear()), []);

  const set = <K extends keyof ComposeForm>(k: K, v: ComposeForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    // 次の下書きを書き始めたら前回の「送りました」は消す（古い結果が新しい下書きの上に残らない）。
    // effect で form.subject を見ると、送った直後に欄を空にしたこと自体で消えてしまうので、打った時だけ消す
    if (k === 'subject' || k === 'text') setDone(null);
    setIssues((list) => list.filter((i) => i.field !== (k === 'date' || k === 'time' || k === 'every' || k === 'mode' ? 'when' : k)));
  };

  const sendBlocks = blocks.filter((b): b is keyof typeof BLOCK_KEYS => b in BLOCK_KEYS);
  const canSend = sendBlocks.length === 0;

  const review = (e: FormEvent) => {
    e.preventDefault();
    const r = buildBroadcast(form, new Date());
    setIssues(r.issues);
    setErr(null);
    if (!r.body) return;
    // 確認を開くたびに新しい鍵（内容を直して開き直したら別の配信）。同じ確認の中の再送は同じ鍵 = サーバーが二重に作らない
    setPending({ body: r.body, key: newIdempotencyKey() });
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    setErr(null);
    try {
      let id = createdRef.current?.key === pending.key ? createdRef.current.id : null;
      if (!id) {
        const res = await api<unknown>(`${mail}/broadcasts`, { method: 'POST', body: pending.body, headers: { 'idempotency-key': pending.key } });
        id = unwrap<Broadcast>(res, 'broadcast')?.id ?? null;
        if (id) createdRef.current = { key: pending.key, id };
      }
      let deferred: ReturnType<typeof sendDeferred> = null;
      if (form.mode === 'now') {
        if (!id) throw new ApiError(0, 'client_error', 'The server did not return the broadcast id');
        const sent = await api<unknown>(`${mail}/broadcasts/${encodeURIComponent(id)}/send`, { method: 'POST', body: {}, headers: { 'idempotency-key': `${pending.key}-send` } });
        // 応答は { broadcast, warmup }。今日の上限を超えて翌日に回った分があれば、送った直後に知らせる（Core の warmup.deferred）
        deferred = sendDeferred(sent);
      }
      setDone({ mode: form.mode, at: pending.body.sendAt ?? null, deferred });
      setPending(null);
      createdRef.current = null;
      setForm((f) => ({ ...f, subject: '', text: '' }));
      onChanged();
    } catch (e2) {
      const ae = e2 as ApiError;
      setErr(ae);
      setPending(null);
      // 差出人情報が（別のタブで消された等で）欠けていた = 差出人のカードを読み直して、欠けた欄を出す
      if (ae.code === 'sender_incomplete') onSenderIncomplete();
      // 作成は済んで送信で失敗した = 下書きが履歴に残る。履歴を読み直して見えるようにする
      if (createdRef.current) onChanged();
    } finally {
      setBusy(false);
    }
  };

  const issueOf = (f: ComposeField) => issues.find((i) => i.field === f);
  const problemText = (f: ComposeField) => {
    const p = issueOf(f);
    if (!p) return null;
    return <small className="field-error">{t(PROBLEM_KEYS[p.problem], { max: f === 'subject' ? MAIL_LIMITS.subject : MAIL_LIMITS.text })}</small>;
  };
  const whenText = (body: BroadcastBody) => {
    if (!body.sendAt) return t('mail.when.now');
    if (body.recurrence) return t('mail.when.recurring', { first: fullDate(body.sendAt), every: everyLabel(body.recurrence.every, body.recurrence.at, body.sendAt) });
    return fullDate(body.sendAt);
  };
  const fromDomain = sendingDomain ?? t('mail.preview.domainPending');
  // 今すぐ送ると今日の残りを超える数（確認のダイアログの「一部は翌日以降」）。予約・定期は送る日の残りが今は分からないので、
  // 今日の上限より多い宛先のときだけ一般的な注意にする（warmupLater）
  const over = form.mode === 'now' ? overToday(warmup, recipients) : 0;
  const laterNote = form.mode !== 'now' && !!warmup && recipients != null && recipients > warmup.dailyLimit;

  return (
    // 確認のダイアログ（ConfirmDialog は中に自分の <form method="dialog"> を持つ）は入力の <form> の外に置く（form の入れ子は HTML で不正）
    <section className="card mail-compose" aria-labelledby="mail-compose-title">
      <h2 className="card-title" id="mail-compose-title">
        {t('mail.compose.title')}
      </h2>
      <form onSubmit={review} noValidate>

      <div className="mail-fields">
        <label className="field">
          <span>{t('mail.compose.subject')}</span>
          <input value={form.subject} maxLength={MAIL_LIMITS.subject + 50} aria-invalid={!!issueOf('subject')} onChange={(e) => set('subject', e.target.value)} />
          {problemText('subject')}
        </label>
        <label className="field">
          <span>
            {t('mail.compose.text')} <small className="muted">{t('mail.compose.textHint')}</small>
          </span>
          <textarea className="textarea" rows={10} value={form.text} aria-invalid={!!issueOf('text')} onChange={(e) => set('text', e.target.value)} />
          {problemText('text')}
        </label>
      </div>

      {/* フッターの見本: 差出人の名前はゲーム名・Kumo は出ない（D3）。フッターは消せない（D6） */}
      <section className="mail-preview" aria-label={t('mail.preview.title')}>
        <h3 className="section-title">{t('mail.preview.title')}</h3>
        <p className="mail-preview-from">
          <span className="muted">{t('mail.preview.from')}</span> <strong>{gameName}</strong> <span className="mono small">&lt;noreply@{fromDomain}&gt;</span>
        </p>
        <p className="mail-preview-subject">
          <span className="muted">{t('mail.preview.subject')}</span> {form.subject.trim() || <span className="muted">{t('mail.preview.noSubject')}</span>}
        </p>
        <div className="mail-preview-body mail-pre">{form.text.trim() ? form.text : <span className="muted">{t('mail.preview.noText')}</span>}</div>
        {/* 文言は Core の compose.ts の footerLines と同じ（本文の後ろに「--」の区切りと 5 行）。停止の URL は宛先ごとに違うので見本では置き換える */}
        <div className="mail-preview-footer mail-pre" aria-label={t('mail.preview.footer')}>
          <span>--</span>
          <span>{t('mail.footer.notice', { game: gameName })}</span>
          <span>{tr('mail.footer.sender', { value: sender?.legalName || <em className="muted">{t('mail.preview.missing.legalName')}</em> })}</span>
          <span>{tr('mail.footer.address', { value: sender?.postalAddress || <em className="muted">{t('mail.preview.missing.postalAddress')}</em> })}</span>
          <span>{tr('mail.footer.contact', { value: sender?.contactEmail || <em className="muted">{t('mail.preview.missing.contactEmail')}</em> })}</span>
          <span>{tr('mail.footer.unsubscribe', { url: <u>{t('mail.preview.unsubscribeUrl')}</u> })}</span>
        </div>
        <p className="small muted">{t('mail.preview.note')}</p>
      </section>

      <fieldset className="mail-audience">
        <legend className="section-title">{t('mail.audience.title')}</legend>
        <div className="form-grid">
          <label className="field">
            <span>{t('mail.audience.language')}</span>
            <select value={form.language} onChange={(e) => set('language', e.target.value)}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l ? `${t(`mail.lang.${l}` as MessageKey)} (${l})` : t('mail.audience.allLanguages')}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('mail.audience.lastPlayed')}</span>
            <select value={form.lastPlayedWithinDays ?? ''} onChange={(e) => set('lastPlayedWithinDays', e.target.value ? Number(e.target.value) : null)}>
              {LAST_PLAYED.map((d) => (
                <option key={d ?? 'all'} value={d ?? ''}>
                  {d ? t('mail.audience.withinDays', { count: d }) : t('mail.audience.anyTime')}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="mail-count" role="status" aria-live="polite">
          {audience.loading && recipients == null
            ? t('mail.audience.counting')
            : recipients == null
              ? t('mail.audience.unknown')
              : tr('mail.audience.count', { count: recipients, n: <strong>{num(recipients)}</strong> })}
        </p>
        <p className="small muted">{t('mail.audience.who')}</p>
      </fieldset>

      <fieldset className="mail-when">
        <legend className="section-title">{t('mail.when.title')}</legend>
        <div className="seg" role="radiogroup" aria-label={t('mail.when.title')}>
          {(['now', 'schedule', 'recurring'] as const).map((m) => (
            <button key={m} type="button" role="radio" aria-checked={form.mode === m} className={`seg-btn ${form.mode === m ? 'is-active' : ''}`} onClick={() => set('mode', m)}>
              {t(`mail.when.mode.${m}`)}
            </button>
          ))}
        </div>
        {form.mode !== 'now' && (
          <div className="mail-when-inputs">
            <p className="small">
              <strong>{t('mail.when.yourTime')}</strong> {tz && <span className="mono muted">{tz}</span>}
            </p>
            <div className="form-grid">
              {form.mode === 'recurring' && (
                <label className="field">
                  <span>{t('mail.when.every')}</span>
                  <select value={form.every} onChange={(e) => set('every', e.target.value as Every)}>
                    {(['day', 'week', 'month'] as const).map((ev) => (
                      <option key={ev} value={ev}>
                        {t(`mail.every.${ev}`)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="field">
                <span>{form.mode === 'recurring' ? t('mail.when.firstDate') : t('mail.when.date')}</span>
                <input type="date" value={form.date} aria-invalid={!!issueOf('when')} onChange={(e) => set('date', e.target.value)} />
              </label>
              <label className="field">
                <span>{t('mail.when.time')}</span>
                <input type="time" value={form.time} aria-invalid={!!issueOf('when')} onChange={(e) => set('time', e.target.value)} />
              </label>
            </div>
            {issueOf('when') && <p className="field-error small">{t(PROBLEM_KEYS[issueOf('when')!.problem])}</p>}
            {form.mode === 'recurring' && (
              <p className="small muted">
                {t(`mail.when.recurringHint.${form.every}`)} {t('mail.when.recurringRule')}
              </p>
            )}
            {form.mode === 'recurring' && dst && <p className="small muted">{t('mail.when.dstNote')}</p>}
          </div>
        )}
        <WarmupBand warmup={warmup} />
      </fieldset>

      {err && (
        <p className="alert alert-error" role="alert">
          {mailErrorText(err)}
        </p>
      )}
      {done && (
        <p className="alert alert-ok" role="status">
          {done.mode === 'now' ? t('mail.done.now') : t('mail.done.scheduled', { at: done.at ? fullDate(done.at) : '—' })}
          {done.deferred && (
            <span className="block">
              {/* identity = 本人確認が済むまでの上限（mailWarmup.limitKind）。「送り始めの」と書かない */}
              {done.deferred.at
                ? t(done.deferred.identity ? 'mail.done.deferredIdentity' : 'mail.done.deferred', { count: done.deferred.deferred, at: fullDate(done.deferred.at) })
                : t(done.deferred.identity ? 'mail.done.deferredNoTimeIdentity' : 'mail.done.deferredNoTime', { count: done.deferred.deferred })}
            </span>
          )}
        </p>
      )}

      {canSend ? (
        <div className="form-actions">
          {nowBlocked(form.mode, recipients) && <span className="small muted mail-zero">{t('mail.compose.zero')}</span>}
          <button className="btn btn-primary" disabled={busy || nowBlocked(form.mode, recipients)}>
            {t(`mail.compose.submit.${form.mode}`)}
          </button>
        </div>
      ) : (
        // 送れない理由があるときは送るボタンを出さない（押してから断られるより、理由と直す場所を先に見せる）
        <div className="alert alert-warn mail-blocked" role="note">
          {sendBlocks.map((b) => (
            <span key={b}>
              {b === 'sender_incomplete' ? tr(BLOCK_KEYS[b], { link: <a href="#mail-sender">{t('mail.sender.title')}</a> }) : t(BLOCK_KEYS[b])}
            </span>
          ))}
        </div>
      )}
      </form>

      <ConfirmDialog
        open={!!pending}
        title={t(`mail.confirm.title.${form.mode}`)}
        confirmLabel={t(`mail.confirm.go.${form.mode}`)}
        busy={busy}
        onConfirm={confirm}
        onClose={() => !busy && setPending(null)}
      >
        {pending && (
          <>
            <dl className="kv">
              <dt>{t('mail.confirm.recipients')}</dt>
              <dd>
                <strong>{recipients == null ? t('mail.confirm.recipientsUnknown') : t('mail.confirm.recipientsN', { count: recipients })}</strong>
              </dd>
              <dt>{t('mail.confirm.subject')}</dt>
              <dd>{pending.body.subject}</dd>
              <dt>{t('mail.confirm.when')}</dt>
              <dd>
                {whenText(pending.body)}
                {pending.body.sendAt && <span className="small muted block">{t('mail.when.yourTime')}</span>}
              </dd>
            </dl>
            {over > 0 && warmup && (
              <p className="alert alert-info" role="note">
                {t('mail.confirm.warmupOver', { remaining: warmup.remainingToday, over })}
              </p>
            )}
            {laterNote && warmup && (
              // 本人確認が済むまでの上限（8 日目以降も続く）は「送り始めて○日間は」と書かない（mailWarmup.limitKind）
              <p className="small muted">{limitKind(warmup) === 'identity' ? t('mail.confirm.identityLater') : t('mail.confirm.warmupLater', { days: warmup.days })}</p>
            )}
            <p className="small muted">{form.mode === 'now' ? t('mail.confirm.noUndo') : t('mail.confirm.cancelLater')}</p>
            <p className="small muted">{t('mail.confirm.cost')}</p>
            {/* 定期は残高切れで取り消され、入金しても再開しない（Core の統合⑰・mailLogic.ts の failureVariant の WHY）。始める前に知らせる */}
            {form.mode === 'recurring' && <p className="small muted">{t('mail.confirm.recurringBalance')}</p>}
          </>
        )}
      </ConfirmDialog>
    </section>
  );
}
